import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import { Code, Palette, Zap, Globe, Smartphone, Search } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Globe,
      title: 'Web Development',
      description: 'Custom websites built with modern technologies for optimal performance and user experience.',
      features: ['React & Next.js', 'Responsive Design', 'SEO Optimized']
    },
    {
      icon: Smartphone,
      title: 'Mobile Apps',
      description: 'Native and cross-platform mobile applications that engage users and drive results.',
      features: ['iOS & Android', 'React Native', 'App Store Deploy']
    },
    {
      icon: Palette,
      title: 'UI/UX Design',
      description: 'Beautiful, intuitive interfaces designed to convert visitors into customers.',
      features: ['User Research', 'Wireframing', 'Prototyping']
    },
    {
      icon: Code,
      title: 'Custom Development',
      description: 'Tailored solutions for complex business requirements and unique challenges.',
      features: ['API Integration', 'Database Design', 'Cloud Infrastructure']
    },
    {
      icon: Zap,
      title: 'Performance Optimization',
      description: 'Speed up your site, improve SEO, and provide better user experience.',
      features: ['Core Web Vitals', 'Lazy Loading', 'CDN Setup']
    },
    {
      icon: Search,
      title: 'SEO & Marketing',
      description: 'Get found online with our comprehensive SEO and digital marketing services.',
      features: ['Keyword Research', 'On-Page SEO', 'Analytics']
    }
  ];

  return (
    <div className="min-h-screen bg-[#030712]">
      <PublicNav />
      
      <section className="pt-32 pb-20 px-6 md:px-12" data-testid="services-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold font-['Syne'] text-white mb-6" data-testid="services-title">
            Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">Services</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto" data-testid="services-description">
            End-to-end digital solutions designed to elevate your business and exceed expectations.
          </p>
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12" data-testid="services-grid">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div 
                key={index}
                className="bg-slate-900/50 border border-white/10 rounded-2xl p-8 hover:border-indigo-500/50 transition-all duration-500 group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-6 group-hover:bg-indigo-500/20 transition-colors">
                  <Icon className="text-indigo-400" size={28} strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-semibold font-['Syne'] text-white mb-4">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-slate-500 flex items-center space-x-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12" data-testid="services-cta">
        <div className="max-w-4xl mx-auto text-center glass rounded-3xl p-12 glow">
          <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
            Need Something Custom?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            We love unique challenges. Let's discuss your project requirements.
          </p>
          <Link 
            to="/contact" 
            className="bg-white text-black hover:bg-slate-200 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 inline-block"
            data-testid="services-contact-btn"
          >
            Contact Us
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12 px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center text-slate-500">
          <p>&copy; 2026 DH HOME NETWORKING AND WEBSITES. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
